import type { GatewayEventHandlerType } from '../generated/types/nodes/ignition-internal'

export interface UrnSegment {
  type: string
  value: string
}

const URN_PREFIX = 'urn:'

/**
 * Encode a value for inclusion in a URN segment. We only encode characters
 * that would otherwise collide with URN structure:
 *
 * - `/` is the segment separator (`urn:compute:foo/tp:bar`), so `/` inside a
 *   value is encoded as `%2F`.
 * - `%` is encoded as `%25` to keep round-tripping clean.
 *
 * Colons are explicitly NOT encoded — RFC 8141 allows colons inside an NSS,
 * and the source-tagged Equipment URNs intentionally use them
 * (`urn:equipment:aveva:<gobjectId>`).
 */
export function encodeUrnValue(value: string): string {
  return value.replace(/%/g, '%25').replace(/\//g, '%2F')
}

export function decodeUrnValue(encoded: string): string {
  return encoded.replace(/%2F/gi, '/').replace(/%25/gi, '%')
}

/**
 * Build a top-level URN of the form `urn:<type>:<encoded-value>`.
 *
 * Intended as the entry point for any URN — child segments get appended via
 * {@link urnAppend} which preserves the `urn:` prefix.
 */
export function urnSegment(type: string, value: string): string {
  return `${URN_PREFIX}${type}:${encodeUrnValue(value)}`
}

/**
 * Append a child segment to an existing URN, preserving the `urn:` prefix.
 * If `parent` does not already start with `urn:` the prefix is added.
 */
export function urnAppend(parent: string, type: string, value: string): string {
  const root = parent.startsWith(URN_PREFIX) ? parent : `${URN_PREFIX}${parent}`
  return `${root}/${type}:${encodeUrnValue(value)}`
}

export function parseUrn(urn: string): UrnSegment[] {
  const stripped = urn.startsWith(URN_PREFIX) ? urn.slice(URN_PREFIX.length) : urn
  return stripped.split('/').map((segment) => {
    const colonIdx = segment.indexOf(':')
    if (colonIdx === -1) {
      return { type: '', value: decodeUrnValue(segment) }
    }
    return {
      type: segment.slice(0, colonIdx),
      value: decodeUrnValue(segment.slice(colonIdx + 1))
    }
  })
}

/**
 * Render a canonical URN as a human-readable label by taking the
 * trailing segment's value and slugging it (hyphens → spaces +
 * title-case). For example:
 *
 *   `urn:equipment:bowling-green-capo-evaporator-113`
 *     → `Bowling Green Capo Evaporator 113`
 *   `urn:org:tyson/site:bowling-green-capo/area:113`
 *     → `113`
 *
 * The trailing segment is chosen because hierarchical URNs encode
 * surrounding context in earlier segments — by the time the URN is
 * displayed (next to a label chip, inside a per-job document panel,
 * etc.) the parent hierarchy is already implied. Callers should keep
 * the raw URN as a `title` tooltip for power users / debugging.
 *
 * Non-URN inputs pass through unchanged (no `urn:` prefix or empty
 * trailing segment) so the helper is safe to apply blindly to any
 * id-or-name string a caller surfaces.
 */
export function humanizeUrn(value: string): string {
  if (!value.startsWith(URN_PREFIX)) return value
  const segments = parseUrn(value)
  const last = segments[segments.length - 1]
  if (!last || !last.value) return value
  return last.value.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// -- Hierarchy roots --

export function organizationUrn(slug: string): string {
  return urnSegment('org', slug)
}

/**
 * Local site slug for ingest / S3 / job rows. Graph Site ids are
 * `urn:org:<org>/site:<slug>` (or legacy `urn:site:<slug>`); passing that
 * URN through as `siteId` nests another `site:` segment and overwrites
 * `Site.name` with the raw URN.
 */
export function siteSlugFromId(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return trimmed
  if (!trimmed.startsWith(URN_PREFIX) && !trimmed.includes('/')) return trimmed
  const site = parseUrn(trimmed)
    .reverse()
    .find((segment) => segment.type === 'site' && segment.value)
  return site?.value || trimmed
}

export function siteUrn(orgSlugOrUrn: string, siteSlug?: string): string {
  if (siteSlug === undefined) {
    // Legacy single-arg call: caller already supplied a site slug only.
    // Returns `urn:site:<slug>` to preserve backward compatibility for
    // callers that don't yet know about the org-scope.
    return urnSegment('site', siteSlugFromId(orgSlugOrUrn))
  }
  const parent =
    orgSlugOrUrn.startsWith(URN_PREFIX) || orgSlugOrUrn.startsWith('org:')
      ? orgSlugOrUrn
      : organizationUrn(orgSlugOrUrn)
  return urnAppend(parent, 'site', siteSlugFromId(siteSlug))
}

/**
 * True when `id` already identifies a Site: the legacy `urn:site:<slug>`
 * form or an org-scoped `urn:org:<org>/site:<slug>` form.
 */
export function isSiteUrn(id: string): boolean {
  if (id.startsWith('urn:site:')) return !id.slice('urn:site:'.length).includes('/')
  return id.startsWith(URN_PREFIX) && /\/site:[^/]+$/.test(id)
}

/**
 * Site URN for an ingest `siteId` that may be either a plain slug or the
 * graph id of an existing Site (the wizard sends the latter when attaching
 * to a site that is already in the graph). Existing URNs pass through so
 * child nodes hang off the real Site instead of a re-wrapped copy of it.
 */
export function resolveSiteUrn(siteIdOrUrn: string): string {
  return isSiteUrn(siteIdOrUrn) ? siteIdOrUrn : siteUrn(siteIdOrUrn)
}

/**
 * Identifier for a SOAP service endpoint owned by an Organization or Site.
 *
 * `endpointIdentity` should be a stable source key such as the canonical
 * endpoint URL. URL path separators are encoded by {@link urnAppend}.
 */
export function soapEndpointUrn(ownerUrn: string, endpointIdentity: string): string {
  return urnAppend(ownerUrn, 'soap-endpoint', endpointIdentity)
}

/**
 * Identifier for a WSDL contract used by one SOAP endpoint.
 *
 * The contract identity is endpoint-scoped because contract names and target
 * namespaces are not guaranteed to be globally unique.
 */
export function soapContractUrn(endpointUrn: string, contractIdentity: string): string {
  return urnAppend(endpointUrn, 'soap-contract', contractIdentity)
}

/**
 * Identifier for an operation declared by a SOAP contract.
 *
 * WSDL operation names are unique within their owning port type/contract, so
 * the parent contract provides the collision boundary.
 */
export function soapOperationUrn(contractUrn: string, operationName: string): string {
  return urnAppend(contractUrn, 'soap-operation', operationName)
}

export function computeUrn(hostOrSlug: string): string {
  return urnSegment('compute', hostOrSlug)
}

/**
 * @deprecated Use {@link computeUrn} (built off `siteUrn`) instead. Kept as a
 * thin alias for legacy callers in `@atbash-labs/ontology-diff` that still
 * compose URNs from the pre-restructure `(siteUrn, gatewayId)` pair.
 *
 * Returns `urn:site:<site>/compute:<gatewayId>` for compatibility with the
 * URN shape the diff processor's existing context (and its on-disk fixtures)
 * already produce.
 */
export function gatewayUrn(siteUrnOrSlug: string, gatewayId: string): string {
  const parent =
    siteUrnOrSlug.startsWith(URN_PREFIX) || siteUrnOrSlug.startsWith('site:')
      ? siteUrnOrSlug
      : siteUrn(siteUrnOrSlug)
  return urnAppend(parent, 'compute', gatewayId)
}

// -- Compute-scoped nodes --

export function tagProviderUrn(computeUrn: string, name: string): string {
  return urnAppend(computeUrn, 'tp', name)
}

export function tagUrn(tpUrn: string, path: string): string {
  return urnAppend(tpUrn, 'tag', path)
}

/**
 * Identifier for an `Alarm` node configured on a `Tag`. Lives under the
 * source tag URN so the structural ownership (one tag → many alarms) is
 * captured by the URN itself and so deletes cascade naturally when the
 * parent tag is removed.
 *
 * `alarmName` is the Ignition alarm's `name` field (entry of the tag's
 * `alarms` array) — alarm names are unique within a tag, so the pair
 * `(tag, alarmName)` is a stable natural key.
 */
export function alarmUrn(tagUrn: string, alarmName: string): string {
  return urnAppend(tagUrn, 'alarm', alarmName)
}

/**
 * Identifier for a `Folder` node hosted under a TagProvider. Folders are
 * per-provider and per-path: one Folder node exists for each distinct
 * provider-relative path. The same `Sensors/` folder under two different
 * providers gets two distinct URNs because the provider scope is in the
 * parent segment.
 *
 * `fullPath` is the slash-joined path from the provider root, with no
 * leading or trailing slash. `/` characters inside the path are encoded by
 * {@link encodeUrnValue}, so a folder named `Sensors/Tank-1` round-trips as
 * `…/tp:default/folder:Sensors%2FTank-1`.
 */
export function folderUrn(tpUrn: string, fullPath: string): string {
  return urnAppend(tpUrn, 'folder', fullPath)
}

/**
 * Identifier for a runtime member `Tag` hanging off a UDT instance.
 * Distinct from {@link tagUrn} (which lives under a TagProvider) so that
 * top-level tags and UDT-instance member tags don't collide in the URN
 * namespace, even though they share the same `:Tag` Neo4j label. The
 * member tag lives under its parent instance so deletes cascade naturally
 * and the structural relationship is captured in the URN itself.
 *
 * Reuses the `tag:` URN segment so tooling that walks `urn:…:tag:*`
 * matches uniformly across top-level and UDT-instance members; the
 * discriminator is the parent segment (`tp:` vs `udt-inst:`).
 */
export function udtMemberTagUrn(udtInstanceUrn: string, memberPath: string): string {
  return urnAppend(udtInstanceUrn, 'tag', memberPath)
}

/**
 * Identifier for a `Database` node hosted by a Compute.
 *
 * Replaces the legacy `dbConnectionUrn(gatewayUrn, name)` helper. The compute_urn
 * argument is the URN of the owning Compute (formerly the gateway).
 */
export function databaseUrn(computeUrn: string, name: string): string {
  return urnAppend(computeUrn, 'db', name)
}

/**
 * @deprecated Use {@link databaseUrn} instead. Kept as an alias for legacy
 * callers in `@atbash-labs/ontology-diff` that still call
 * `dbConnectionUrn(...)`.
 */
export function dbConnectionUrn(computeUrn: string, name: string): string {
  return databaseUrn(computeUrn, name)
}

export function tableUrn(dbUrn: string, schemaAndName: string): string {
  return urnAppend(dbUrn, 'table', schemaAndName)
}

// -- Database registry (db:// data points) --
//
// These back the deterministic graph `:Tag`/`:Database` nodes created at
// registration time for proxy `db://{connection}/{datapoint}` data points (see
// `@atbash-labs/ontology-db` `buildDbTagPath`, the paired Postgres/live-data
// identity). They are keyed purely on the connection name + datapoint from the
// `db://` grammar and are deliberately NOT hung under a Compute — a registry db
// connection is not necessarily tied to a gateway/Compute the way an Ignition
// datasource is. That makes them distinct from `databaseUrn(computeUrn, name)`,
// which addresses a `:Database` discovered under a Compute by Ignition serdes.

/**
 * Identifier for the `:Database` node of a registered `db://` connection.
 *
 * Self-contained (`urn:db:<connection>`); the connection name is the same one
 * used by `buildDbTagPath(connection, datapoint)` in `@atbash-labs/ontology-db`.
 */
export function dbRegistryDatabaseUrn(connection: string): string {
  return urnSegment('db', connection)
}

/**
 * Shape of a registry connection name (`urn:db:<connection>`,
 * `duckdb://<connection>`): a stable, retargetable database name such as
 * `orders-dump` or `tba_daily_311d`, not a SQL identifier. Table names inside a
 * dump stay identifiers. Mirrored by `$defs.connectionName` in the proxy's
 * `mapped_tabular_ingest.v1.schema.json`.
 */
export const DB_REGISTRY_CONNECTION_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/

export function isDbRegistryConnectionName(value: string): boolean {
  return DB_REGISTRY_CONNECTION_NAME_RE.test(value)
}

/**
 * True for a self-contained registry `:Database` id (`urn:db:<connection>`).
 * Rejects datapoint Tags (`urn:db:<connection>/datapoint:…`) and
 * Compute-scoped catalogs (`…/db:<name>`).
 */
export function isDbRegistryDatabaseUrn(id: string): boolean {
  const segments = parseUrn(id)
  return segments.length === 1 && segments[0]?.type === 'db' && segments[0].value.length > 0
}

/**
 * Identifier for the deterministic `:Tag` node of a registered `db://` data
 * point. Hangs under {@link dbRegistryDatabaseUrn} so two datapoints on the same
 * connection share a `:Database` parent, and the same datapoint on two
 * connections yields two distinct `:Tag` ids.
 *
 * `urn:db:<connection>/datapoint:<datapoint>` (slashes in the datapoint are
 * percent-encoded). Round-trips through {@link parseUrn}.
 */
export function dbTagUrn(connection: string, datapoint: string): string {
  return urnAppend(dbRegistryDatabaseUrn(connection), 'datapoint', datapoint)
}

// -- PSS mirror projections (PssTag / PssEventJournal) --
//
// A Hexagon PlantState Suite mirror is registered as a standalone
// `dbRegistryDatabaseUrn(connection)` `:Database`. Its tags and event journals
// are projected under that Database, scoped by the mirror's data owner so the
// same tag name under two owners yields two distinct nodes. `ownerKey` is the
// proxy's stable per-owner key (`DataOwnerRef.Key()`: the PSS owner id, else
// its name), which is also how the mirror scopes rows — so a renamed owner
// keeps its identity.

function pssDataOwnerUrn(connection: string, ownerKey: string): string {
  return urnAppend(dbRegistryDatabaseUrn(connection), 'owner', ownerKey)
}

/**
 * Identifier for the `:PssTag` node of one mirrored PSS tag.
 *
 * `urn:db:<connection>/owner:<ownerKey>/psstag:<name>`; `name` is the verbatim
 * PSS tag name (slashes percent-encoded). Round-trips through {@link parseUrn}.
 */
export function pssTagUrn(connection: string, ownerKey: string, tagName: string): string {
  return urnAppend(pssDataOwnerUrn(connection, ownerKey), 'psstag', tagName)
}

/**
 * Identifier for the `:PssEventJournal` node of one data owner's journal of
 * one kind (Alarms / Changes / System) — i.e. one physical
 * `journal_<kind>_<owner>_<hash>` table on the mirror.
 *
 * `urn:db:<connection>/owner:<ownerKey>/journal:<kind>`. The kind is trimmed
 * and lower-cased, matching how the proxy derives the physical table identity.
 */
export function pssEventJournalUrn(
  connection: string,
  ownerKey: string,
  journalKind: string
): string {
  return urnAppend(
    pssDataOwnerUrn(connection, ownerKey),
    'journal',
    journalKind.trim().toLowerCase()
  )
}

/** True for a `:PssTag` id built by {@link pssTagUrn}. */
export function isPssTagUrn(id: string): boolean {
  const segments = parseUrn(id)
  return (
    segments.length === 3 &&
    segments[0]?.type === 'db' &&
    segments[0].value.length > 0 &&
    segments[1]?.type === 'owner' &&
    segments[1].value.length > 0 &&
    segments[2]?.type === 'psstag' &&
    segments[2].value.length > 0
  )
}

// -- Process / Line --

export function processUrn(orgUrn: string, slug: string): string {
  return urnAppend(orgUrn, 'process', slug)
}

export function lineUrn(siteUrn: string, slug: string): string {
  return urnAppend(siteUrn, 'line', slug)
}

/** Organization-scoped identity for a finished Product. */
export function productUrn(orgUrn: string, productId: string): string {
  return urnAppend(orgUrn, 'product', productId)
}

/**
 * Line-scoped identity for a scenario ProductionGoal. Including the scenario
 * keeps two runs' goals distinct even when they reuse the same local goal id.
 */
export function productionGoalUrn(lineUrn: string, scenarioId: string, goalId: string): string {
  return urnAppend(lineUrn, 'goal', `${scenarioId}:${goalId}`)
}

/**
 * Site-scoped URN for a plant spatial Area (`Area` -[:PART_OF]-> `Site`).
 */
export function areaUrn(siteUrn: string, slug: string): string {
  return urnAppend(siteUrn, 'area', slug)
}

/**
 * Site-scoped URN for a `ProtectiveFunction`. `sifNo` is the SRS primary key
 * (e.g. `12PALL411220`) and is unique inside one SRS database, not globally.
 */
export function protectiveFunctionUrn(siteUrn: string, sifNo: string): string {
  return urnAppend(siteUrn, 'pf', sifNo)
}

/**
 * Site-scoped URN for a `LogicSolver`. `systemId` is the SRS SystemID
 * (e.g. `12-RIE-4101`) and is unique inside one plant, not globally.
 */
export function logicSolverUrn(siteUrn: string, systemId: string): string {
  return urnAppend(siteUrn, 'ls', systemId)
}

/** Site-scoped URN for an SRS document chapter (`SRS1.IDSRS1`). */
export function srsSectionUrn(siteUrn: string, idSrs1: string): string {
  return urnAppend(siteUrn, 'srs-section', idSrs1)
}

/** Site-scoped URN for an SRS bibliography entry (`Documents.DocumentID` under a package). */
export function referenceDocumentUrn(
  siteUrn: string,
  packageId: string,
  documentId: string
): string {
  return urnAppend(siteUrn, 'ref-doc', `${packageId}:${documentId}`)
}

/** Site-scoped URN for a HAZOP / PHA scenario token from `SIFSRS.Scenario`. */
export function hazardScenarioUrn(siteUrn: string, scenarioId: string): string {
  return urnAppend(siteUrn, 'scenario', scenarioId)
}

/**
 * Logic-solver-scoped URN for a Safety Manager Function Logic Diagram
 * (`FLDNumber` in the points dump).
 */
export function functionLogicDiagramUrn(logicSolverUrn: string, fldNumber: string): string {
  return urnAppend(logicSolverUrn, 'fld', fldNumber)
}

/**
 * Logic-solver-scoped URN for a Safety Manager application point
 * (`TagNumber` in the points dump).
 */
export function safetyPointUrn(logicSolverUrn: string, tagNumber: string): string {
  return urnAppend(logicSolverUrn, 'spoint', tagNumber)
}

/**
 * Source-tagged Line URN for AVEVA-derived Lines (i.e. AVEVA `gobjects`
 * whose derivation chain reaches `$Area`). Parallels {@link avevaEquipmentUrn};
 * the AI dedup pass collapses these onto a canonical site-scoped
 * `lineUrn(siteUrn, slug)` form before downstream consumers see them.
 */
export function avevaLineUrn(gobjectId: string): string {
  return urnSegment('line', `aveva:${gobjectId}`)
}

/**
 * Line-scoped URN for a `LineOperation` (concrete ordered step on a Line).
 */
export function lineOperationUrn(lineUrn: string, slug: string): string {
  return urnAppend(lineUrn, 'operation', slug)
}

/**
 * Canonical URN for a `HumanLabor` contribution. Top-level (like {@link equipmentUrn})
 * so a single contribution definition can be referenced from multiple
 * LineOperations / Lines via `REQUIRES_LABOR` — per-step simultaneous demand
 * (`num_employees`) lives on that edge, not in the node identity.
 */
export function humanLaborUrn(slug: string): string {
  return urnSegment('human-labor', slug)
}

/**
 * Line-scoped URN for a `CrewRequirement` (shared staffing capacity covering
 * one or more HumanLabor contributions on that Line).
 */
export function crewRequirementUrn(lineUrn: string, slug: string): string {
  return urnAppend(lineUrn, 'crew', slug)
}

// -- Asset domain --

/**
 * Canonical Equipment URN. The dedup pass converges source-tagged URNs
 * (`urn:equipment:aveva:<id>`, `urn:equipment:ignition:<id>`, etc.) onto this form.
 */
export function equipmentUrn(slug: string): string {
  return urnSegment('equipment', slug)
}

export function avevaEquipmentUrn(gobjectId: string): string {
  return urnSegment('equipment', `aveva:${gobjectId}`)
}

export function ignitionEquipmentUrn(udtInstanceId: string): string {
  return urnSegment('equipment', `ignition:${udtInstanceId}`)
}

export function aiFolderEquipmentUrn(hash: string): string {
  return urnSegment('equipment', `ai-folder:${hash}`)
}

export function manualEquipmentUrn(slug: string): string {
  return urnSegment('equipment', `manual:${slug}`)
}

/**
 * Source-tagged Equipment URN for SRS-derived devices (plant tag identity).
 * Later sources MERGE on `tag_number`; the AI dedup pass may collapse these
 * onto {@link equipmentUrn}.
 */
export function srsEquipmentUrn(tag: string): string {
  return urnSegment('equipment', `srs:${tag}`)
}

export function partUrn(equipmentUrn: string, slug: string): string {
  return urnAppend(equipmentUrn, 'part', slug)
}

export function observableUrn(equipmentOrPartUrn: string, name: string): string {
  return urnAppend(equipmentOrPartUrn, 'obs', name)
}

export function setpointUrn(equipmentOrPartUrn: string, name: string): string {
  return urnAppend(equipmentOrPartUrn, 'sp', name)
}

export function equipmentPropertyUrn(equipmentOrPartUrn: string, name: string): string {
  return urnAppend(equipmentOrPartUrn, 'prop', name)
}

// -- Compute-scoped Ignition-internal --

export function auditProfileUrn(computeUrn: string, name: string): string {
  return urnAppend(computeUrn, 'audit', name)
}

/**
 * Identifier for an Ignition `ScheduleProfile` node — gateway-wide, so
 * scoped under the owning `Compute` URN.
 */
export function scheduleProfileUrn(computeUrn: string, name: string): string {
  return urnAppend(computeUrn, 'schedule', name)
}

/**
 * Identifier for an Ignition `Holiday` node — gateway-wide, so scoped
 * under the owning `Compute` URN.
 */
export function holidayUrn(computeUrn: string, name: string): string {
  return urnAppend(computeUrn, 'holiday', name)
}

export function udtDefUrn(computeUrn: string, name: string): string {
  return urnAppend(computeUrn, 'udt-def', name)
}

export function udtInstanceUrn(computeUrn: string, path: string): string {
  return urnAppend(computeUrn, 'udt-inst', path)
}

export function udtMemberUrn(parentUrn: string, memberName: string): string {
  return urnAppend(parentUrn, 'member', memberName)
}

export function udtParameterUrn(parentUrn: string, paramName: string): string {
  return urnAppend(parentUrn, 'param', paramName)
}

// -- Project-scoped Ignition-internal --

export function projectUrn(computeUrn: string, project: string): string {
  return urnAppend(computeUrn, 'project', project)
}

export function scriptUrn(projUrn: string, path: string): string {
  return urnAppend(projUrn, 'script', path)
}

export function viewUrn(projUrn: string, path: string): string {
  return urnAppend(projUrn, 'view', path)
}

export function viewComponentUrn(vUrn: string, path: string): string {
  return urnAppend(vUrn, 'component', path)
}

export function namedQueryUrn(projUrn: string, path: string): string {
  return urnAppend(projUrn, 'nq', path)
}

export function gatewayEventUrn(projUrn: string, name: string): string {
  return urnAppend(projUrn, 'gw-event', name)
}

export function gatewayEventHandlerUrn(
  eventUrn: string,
  handlerType: GatewayEventHandlerType,
  name: string
): string {
  return urnAppend(eventUrn, 'handler', `${handlerType}/${name}`)
}

// -- PLC-scoped nodes (unchanged) --

export function controllerUrn(siteUrn: string, name: string): string {
  return urnAppend(siteUrn, 'ctrl', name)
}

export function programUrn(ctrlUrn: string, name: string): string {
  return urnAppend(ctrlUrn, 'prog', name)
}

export function routineUrn(parentUrn: string, name: string): string {
  return urnAppend(parentUrn, 'routine', name)
}

export function aoiUrn(ctrlUrn: string, name: string): string {
  return urnAppend(ctrlUrn, 'aoi', name)
}

/**
 * PLC data type ids retain the legacy `udt-def` segment so relabeling existing
 * graph nodes does not change their identity.
 */
export function plcDataTypeUrn(ctrlUrn: string, name: string): string {
  return udtDefUrn(ctrlUrn, name)
}

/**
 * PLC data type member ids retain the legacy `member` segment for identity stability.
 */
export function plcDataTypeMemberUrn(dataTypeUrn: string, memberName: string): string {
  return udtMemberUrn(dataTypeUrn, memberName)
}

export function plcTagUrn(parentUrn: string, name: string): string {
  return urnAppend(parentUrn, 'plctag', name)
}

export function controlPatternUrn(parentUrn: string, name: string): string {
  return urnAppend(parentUrn, 'pattern', name)
}

export function dataFlowUrn(parentUrn: string, name: string): string {
  return urnAppend(parentUrn, 'flow', name)
}

// -- Robotics-scoped nodes (Motoman/Yaskawa controller backups) --

/**
 * Identifier for a `RobotController` node — the physical controller cabinet
 * (Motoman DX/YRC/FS-series) that hosts one or more robot arms and owns the
 * shared job/signal/condition resources of a backup. Scoped under the owning
 * `Site` so two controllers at different sites never collide.
 */
export function robotControllerUrn(siteUrn: string, controllerId: string): string {
  return urnAppend(siteUrn, 'robotctrl', controllerId)
}

/**
 * Identifier for a `RobotArm` node — one mechanical manipulator hosted by a
 * controller. `group` is the Motoman robot-group token (`R1`, `R2`, `GROUP1`,
 * …) which is stable within a controller.
 */
export function robotArmUrn(robotCtrlUrn: string, group: string): string {
  return urnAppend(robotCtrlUrn, 'arm', group)
}

/**
 * Identifier for a `RobotJob` node — one `.JBI` job program owned by a
 * controller. Job names are unique within a controller, so the pair
 * (controller, jobName) is a stable natural key.
 */
export function robotJobUrn(robotCtrlUrn: string, jobName: string): string {
  return urnAppend(robotCtrlUrn, 'job', jobName)
}

/**
 * Identifier for a `RobotIOSignal` node — one named I/O signal from the
 * controller's `IONAME.DAT`. `address` is the controller signal address
 * (e.g. `IN0001`, `OT0010`), which is unique within a controller.
 */
export function robotIOSignalUrn(robotCtrlUrn: string, address: string): string {
  return urnAppend(robotCtrlUrn, 'iosig', address)
}

/**
 * Identifier for a `RobotCondition` node — one welding/work condition index
 * from the controller's `.CND` / weld `.DAT` files. `conditionId` is the
 * condition's scheme-qualified index (e.g. `ARCSTART#1`, `WEAV#3`).
 */
export function robotConditionUrn(robotCtrlUrn: string, conditionId: string): string {
  return urnAppend(robotCtrlUrn, 'cond', conditionId)
}

/**
 * Identifier for a `VisionProcess` node — one robot vision process (FANUC
 * iRVision) on a controller. `name` is the vision data name job code runs
 * (`VISION RUN_FIND 'NAME'`); vision data names are case-insensitive on the
 * controller, so callers pass the upper-cased name.
 */
export function visionProcessUrn(robotCtrlUrn: string, name: string): string {
  return urnAppend(robotCtrlUrn, 'visionproc', name)
}

/**
 * Identifier for a `VisionData` node — a non-process vision data item (camera
 * setup, camera calibration, vision override, …) on a controller. Kept in a
 * separate URN segment from {@link visionProcessUrn} because the label differs.
 */
export function visionDataUrn(robotCtrlUrn: string, name: string): string {
  return urnAppend(robotCtrlUrn, 'visiondata', name)
}

// -- Vision-scoped nodes (Cognex In-Sight / DataMan / VisionPro backups) --

/**
 * Identifier for a `VisionSystem` node — one camera / vision controller /
 * code reader at a Site, keyed by its device (host) name from the backup.
 */
export function visionSystemUrn(siteUrn: string, systemName: string): string {
  return urnAppend(siteUrn, 'vision', systemName)
}

/** Identifier for a `VisionJob` owned by a vision system; job file names are unique per device. */
export function visionJobUrn(visionSystemUrn: string, jobName: string): string {
  return urnAppend(visionSystemUrn, 'visionjob', jobName)
}

// -- DCS-scoped nodes (Experion ERDB and vendor-neutral DCS ingest) --

/**
 * Identifier for a `DcsController` node — a C300 / UOC / CN100 / … cabinet
 * at a Site. Distinct from PLC `controllerUrn` (`ctrl`) and robot
 * `robotControllerUrn` (`robotctrl`).
 */
export function dcsControllerUrn(siteUrn: string, name: string): string {
  return urnAppend(siteUrn, 'dcsctrl', name)
}

/** Identifier for a `Cee` (Control Execution Environment) hosted by a DcsController. */
export function ceeUrn(dcsCtrlUrn: string, name: string): string {
  return urnAppend(dcsCtrlUrn, 'cee', name)
}

/** Identifier for a `ControlModule` (CM or SCM) assigned to a Cee. */
export function controlModuleUrn(ceeUrn: string, name: string): string {
  return urnAppend(ceeUrn, 'cm', name)
}

/** Identifier for a `FunctionBlock` contained by a ControlModule, Cee, DcsController, or parent FunctionBlock. */
export function functionBlockUrn(parentUrn: string, name: string): string {
  return urnAppend(parentUrn, 'fb', name)
}

/** Identifier for an `IoModule` (physical I/O card) owned by a DcsController. */
export function ioModuleUrn(dcsCtrlUrn: string, name: string): string {
  return urnAppend(dcsCtrlUrn, 'iomod', name)
}

/** Identifier for an `IoChannel` terminal on an IoModule. */
export function ioChannelUrn(ioModuleUrn: string, name: string): string {
  return urnAppend(ioModuleUrn, 'ioch', name)
}

/**
 * Identifier for a `ControlNamespace` — the Experion point namespace under
 * a Compute. Distinct from `tagProviderUrn` (`tp`).
 */
export function controlNamespaceUrn(computeUrn: string, name: string): string {
  return urnAppend(computeUrn, 'cns', name)
}

/** Identifier for a published `ControlTag` (CM.PV / CM.OP / CM.SP). */
export function controlTagUrn(namespaceUrn: string, pointName: string): string {
  return urnAppend(namespaceUrn, 'ctag', pointName)
}

/** Identifier for a `ControlAlarm` configured on a ControlTag. */
export function controlAlarmUrn(controlTagUrn: string, alarmName: string): string {
  return urnAppend(controlTagUrn, 'calarm', alarmName)
}

/** Identifier for a `ControlSetpoint` bound to a ControlTag. */
export function controlSetpointUrn(controlTagUrn: string, name: string): string {
  return urnAppend(controlTagUrn, 'csp', name)
}

/** Identifier for a catalog `BlockLibrary`. */
export function blockLibraryUrn(name: string, release?: string): string {
  return urnSegment('blocklib', release ? `${name}@${release}` : name)
}

/** Identifier for a catalog `BlockType` inside a BlockLibrary. */
export function blockTypeUrn(libraryUrn: string, classOrTemplate: string): string {
  return urnAppend(libraryUrn, 'btype', classOrTemplate)
}

/**
 * Site-scoped URN for an enterprise/PCN `Asset` from the Experion EMDB asset
 * hierarchy. `slug` is normally the `FULLITEMNAME` path (e.g. `Assets/PADC`),
 * falling back to GUID or StrategyName. Distinct from process `equipmentUrn`.
 */
export function assetUrn(siteUrn: string, slug: string): string {
  return urnAppend(siteUrn, 'asset', slug)
}

/** Site-scoped URN for a PCN `NetworkSegment` (segment / FTE community). */
export function networkSegmentUrn(siteUrn: string, slug: string): string {
  return urnAppend(siteUrn, 'netseg', slug)
}

/** Site-scoped URN for a PCN `NetworkDevice` (switch / FTE device / net equip). */
export function networkDeviceUrn(siteUrn: string, slug: string): string {
  return urnAppend(siteUrn, 'netdev', slug)
}

/**
 * Site-scoped URN for an Experion EMDB `EmdbProject` (System Management
 * folder: Asset, CANE, System, …). Distinct from Ignition Perspective
 * project identity.
 */
export function emdbProjectUrn(siteUrn: string, slug: string): string {
  return urnAppend(siteUrn, 'emdbproj', slug)
}

/** Identifier for a Control Builder parameter tab on a catalog `BlockType`. */
export function blockParamGroupUrn(blockTypeUrn: string, groupKey: string): string {
  return urnAppend(blockTypeUrn, 'bpgroup', groupKey)
}
